/**
 * Investment math utilities.
 * Business constants are centralised in @/config/investment.js.
 * COEFFICIENT_LEVELS and INVESTMENT_PRESETS are re-exported here
 * for components that already import them from this module.
 */

import { COEFFICIENT_LEVELS, INVESTMENT_PRESETS } from '@/config/investment';
export { COEFFICIENT_LEVELS, INVESTMENT_PRESETS };

/**
 * Calculates how an investment is broken down across different coefficient levels
 * @param {number} amount - The amount to invest
 * @param {number} currentTotal - Current total investments in the system
 * @param {Array} levels - Optional custom levels
 * @returns {Array} breakdown of investment parts
 */
export function calculateInvestmentBreakdown(amount, currentTotal, levels = COEFFICIENT_LEVELS) {
    let breakdown = [];
    let remainingAmount = Number(amount);
    let processedTotal = Number(currentTotal);

    for (let level of levels) {
        if (remainingAmount <= 0) break;

        const availableAtLevel = Math.max(0, level.threshold - processedTotal);
        if (availableAtLevel > 0) {
            const amountAtLevel = Math.min(remainingAmount, availableAtLevel);

            breakdown.push({
                amount: amountAtLevel,
                coefficient: level.coefficient,
                payout: amountAtLevel * level.coefficient,
                levelThreshold: level.threshold
            });

            remainingAmount -= amountAtLevel;
            processedTotal += amountAtLevel;
        }
    }

    
    if (remainingAmount > 0) {
        // If all level thresholds are exhausted, apply the last defined coefficient.
        const lastLevel = levels[levels.length - 1];
        const fallbackCoefficient = lastLevel ? lastLevel.coefficient : 1.5;

        breakdown.push({
            amount: remainingAmount,
            coefficient: fallbackCoefficient,
            payout: remainingAmount * fallbackCoefficient,
            levelThreshold: Infinity
        });
    }

    return breakdown;
}

export function calculatePayoutClaim(breakdown) {
    return breakdown.reduce((sum, item) => sum + item.payout, 0);
}

export function formatCurrency(amount, decimals = 2) {
    if (amount == null || amount === '') return '0€';
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numAmount)) return '0€';
    return new Intl.NumberFormat('sk-SK', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    }).format(numAmount);
}
